import { Controller } from 'stimulus';
import LocomotiveScroll from 'locomotive-scroll';
import getControllerForElement from '../helpers/getControllerForElement';

/**
 * @typedef {Object} LocomotiveScrollElement
 * @property {HTMLElement} el
 * @property {float} progress
 */

class ScrollController extends Controller {
  connect() {
    this.scroll = new LocomotiveScroll({
      resetNativeScroll: false,
    });

    this.controllerCache = {};

    this.scroll.on('scroll', this.onLocomotiveScroll.bind(this));
    this.scroll.on('call', this.onLocomotiveCall.bind(this));

    window.addEventListener('load', () => {
      // this.scroll.update();
    });
  }

  disconnect() {
    if (this.scroll) {
      this.scroll.destroy();
    }

    this.controllerCache = {};
  }

  /**
   * Event is called on every scroll movement from LocomotiveScroll.
   * currentElements hold the elements that are currently in the viewport.
   *
   * @param {LocomotiveScrollElement[]} currentElements
   */
  onLocomotiveScroll({ currentElements }) {
    if (!Object.keys(currentElements).length) {
      return;
    }

    // Loop over every element in the viewport
    Object.keys(currentElements).forEach((currentElementKey) => {
      const currentElementObject = currentElements[currentElementKey];
      const currentElementNode = currentElementObject.el;

      // Retrieve the controllers on the element and call the controller.
      this.resolveControllers(currentElementNode, (controller) => {
        ScrollController.callScrollOnController(
          controller,
          currentElementObject,
        );
      });
    });
  }

  /**
   * Called when an element with data-scroll-call is in the viewport. This
   * event is called only on entering the viewport. So not on every scrolled
   * pixel like 'scroll' above.
   *
   * @param {string} func
   * @param {string} type (e.g. 'enter')
   * @param {LocomotiveScrollElement} currentElementObject
   */
  onLocomotiveCall(func, type, currentElementObject) {
    const currentElementNode = currentElementObject.el;

    // Func is the value that we passed in data-scroll-call. By defining
    // 'controller' we basically mean, call the Stimulus controllers on this element.
    if (func === 'controller') {
      // Find all all controllers on the element and call the scrollHitHandler
      // on every controller (if the function is available).
      this.resolveControllers(currentElementNode, (controller) => {
        ScrollController.callScrollHitOnController(
          controller,
          currentElementObject,
          type,
        );
      });
    }
  }

  /**
   * Retrieve controller instances for every controller defined on the element.
   * For every controller the callback is called. This is asynchronous because
   * the controller could be unavailable for sometime (depending on loading order).
   *
   * @param {HTMLElement} currentElementNode
   * @param {Function} callback
   */
  resolveControllers(currentElementNode, callback) {
    // An ID is used to cache the controller instances. resolveController
    // might be called on every scroll and thus could be heavy. By caching
    // the controllers for the element, we won't have to retrieve the
    // controller on every call.
    const id = currentElementNode.getAttribute('data-id');

    if (!currentElementNode.getAttribute('data-controller')) {
      return;
    }

    // Get an array of all controller names from the element.
    const controllers = currentElementNode.getAttribute('data-controller').split(' ');

    controllers.forEach((controllerName) => {
      const cacheKey = `${id}:${controllerName}`;

      // If the controller is already cached, return its instance.
      if (typeof this.controllerCache[cacheKey] !== 'undefined') {
        callback(this.controllerCache[cacheKey]);
        return;
      }

      // Retrieve the controller instance from the element.
      getControllerForElement(this.application, currentElementNode, controllerName)
        .then((controller) => {
          // Add to cache
          this.controllerCache[cacheKey] = controller;

          // Return the controller instance
          callback(this.controllerCache[cacheKey]);
        });
    });
  }

  /**
   * @param {Controller} controller
   * @param {LocomotiveScrollElement} currentElementObject
   */
  static callScrollOnController(controller, currentElementObject) {
    if (typeof controller.scrollHandler === 'function') {
      controller.scrollHandler.call(controller, currentElementObject);
    }
  }

  /**
   * @param {Controller} controller
   * @param {LocomotiveScrollElement} currentElementObject
   * @param {string} type (e.g. 'enter')
   */
  static callScrollHitOnController(controller, currentElementObject, type) {
    if (typeof controller.scrollHitHandler === 'function') {
      controller.scrollHitHandler.call(controller, currentElementObject, type);
    }
  }
}

export default ScrollController;
